import { BuilderElement, ElementType, GlobalConfig } from "@/types/builder";
import {
  createTestimonialsHeaderSection,
  createMentionedInSection,
  createListingSection,
  createFooterMenuSection,
  createFooterEndSection,
  createPartnerMenuSection,
} from "./template2";

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const template2DownsellGlobalConfig: GlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Inter",
      fontSize: "40px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Inter",
      fontSize: "28px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Inter",
      fontSize: "18px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "700",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#F3F6FF",
    forground: "#FFFFFF",
    primary: "#A44CF5",
    secondary: "#317CF7",
    tertiary: "#333333",
    quaternary: "#1a1a1a",
    link: "#A44CF5",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};

const template2DownsellFeature = (globalConfig: GlobalConfig) => {
  return {
    id: generateId(),
    type: "container" as ElementType,
    styles: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "30px",
      marginTop: "0",
      padding: "0 30px",
    },
    responsiveStyles: {
      tablet: { padding: "0 20px" },
      mobile: { padding: "0 16px" },
    },
    children: [
      {
        id: generateId(),
        type: "container" as ElementType,
        styles: {
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "30px",
        },
        responsiveStyles: {
          tablet: { gap: "16px" },
        },
        children: [
          // Feature Box
          {
            id: generateId(),
            type: "container" as ElementType,
            styles: {
              width: "100%",
            },
            children: [
              // Feature Header
              {
                id: generateId(),
                type: "container" as ElementType,
                styles: {
                  backgroundImage: "url('/images/movyo/upsell/featureBox.svg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  padding: "32px 0",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                },
                responsiveStyles: {
                  tablet: { padding: "16px 0" },
                },
                children: [
                  {
                    id: generateId(),
                    type: "heading" as ElementType,
                    content:
                      "Get 2 More Bottles of Movyo for Just $59 Total (Only $29.50 Each)",
                    dynamicContentId: "__dynamic__bottle_count__",
                    styles: {
                      maxWidth: "926px",
                      color: "background",
                      textAlign: "center",
                      fontSize: "40px",
                      fontWeight: "700",
                      lineHeight: "48px",
                      margin: "0",
                    },
                    typography: "Heading-2",
                    responsiveStyles: {
                      tablet: { fontSize: "24px", lineHeight: "28px" },
                      mobile: { fontSize: "20px", lineHeight: "26px" },
                    },
                  },
                ],
              },
              // Feature Body
              {
                id: generateId(),
                type: "container" as ElementType,
                styles: {
                  padding: "104px 40px 98px",
                  backgroundColor: "#F9F9F9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                responsiveStyles: {
                  tablet: {
                    flexDirection: "column",
                    padding: "20px",
                    alignItems: "center",
                    gap: "20px",
                  },
                  mobile: {
                    flexDirection: "column",
                    padding: "10px",
                    alignItems: "center",
                    gap: "20px",
                  },
                },
                children: [
                  // Image Container
                  {
                    id: generateId(),
                    type: "container" as ElementType,
                    styles: {
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                    },
                    children: [
                      {
                        id: generateId(),
                        type: "container" as ElementType,
                        styles: {
                          width: "469.365px",
                          height: "442px",
                          flexShrink: 0,
                          position: "relative",
                        },
                        responsiveStyles: {
                          tablet: { width: "300px", height: "auto" },
                          mobile: { width: "100%", height: "100%" },
                        },
                        children: [
                          {
                            id: generateId(),
                            type: "image" as ElementType,
                            dynamicContentId: "__dynamic__bottle_image__",
                            src: "/images/movyo/dynamic-images/bottles-2.webp",
                            alt: "Bottles",
                            styles: {
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            },
                          },
                        ],
                      },
                    ],
                  },
                  // Content Body
                  {
                    id: generateId(),
                    type: "container" as ElementType,
                    styles: {
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      gap: "30px",
                      flex: 1,
                    },
                    responsiveStyles: {
                      tablet: { gap: "16px", alignItems: "center" },
                      mobile: { alignItems: "center" },
                    },
                    children: [
                      {
                        id: generateId(),
                        type: "heading" as ElementType,
                        content: "Perfect for continued results",
                        styles: {
                          color: "link",
                          fontFamily: "Inter",
                          fontSize: "30px",
                          fontWeight: "600",
                          lineHeight: "100%",
                          margin: "0",
                        },
                        responsiveStyles: {
                          tablet: { textAlign: "center" },
                          mobile: {
                            fontSize: "28px",
                            lineHeight: "32px",
                            textAlign: "center",
                          },
                        },
                      },
                      // Feature List
                      {
                        id: generateId(),
                        type: "list" as ElementType,
                        styles: {
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                          gap: "20px",
                        },
                        responsiveStyles: {
                          tablet: { gap: "16px" },
                        },
                        children: [
                          {
                            id: generateId(),
                            type: "listItem" as ElementType,
                            content: `<img src="/images/movyo/✓.svg" alt="check" style="width: 30px; height: 30px;" /> That's a massive 50% discount off regular price`,
                            styles: {
                              color: "link",
                              fontSize: "24px",
                              fontWeight: "600",
                              lineHeight: "26px",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                            },
                            responsiveStyles: {
                              tablet: { fontSize: "22px", lineHeight: "30px" },
                              mobile: { fontSize: "18px", lineHeight: "24px" },
                            },
                          },
                          {
                            id: generateId(),
                            type: "listItem" as ElementType,
                            content: `<img src="/images/movyo/✓.svg" alt="check" style="width: 30px; height: 30px;" /> Ideal for maintaining your progress and seeing real results`,
                            styles: {
                              color: "link",
                              fontSize: "24px",
                              fontWeight: "600",
                              lineHeight: "26px",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                            },
                            responsiveStyles: {
                              tablet: { fontSize: "22px", lineHeight: "30px" },
                              mobile: { fontSize: "18px", lineHeight: "24px" },
                            },
                          },
                        ],
                      },
                      // CTA Button
                      {
                        id: generateId(),
                        type: "button" as ElementType,
                        content: "YES, ADD 2 MORE BOTTLES FOR JUST $59",
                        dynamicContentId: "__dynamic__button_offer__",
                        styles: {
                          backgroundColor: "#F4B83D", // $primary-color
                          color: "forground", // $black
                          fontWeight: "700",
                          fontSize: "20px",
                          lineHeight: "24px",
                          padding: "10.5px 27.5px",
                          borderRadius: "24px", // $radius24
                          border: "none",
                          cursor: "pointer",
                          textTransform: "capitalize",
                          transition: "all 0.3s ease-in-out",
                        },
                        typography: "Button",
                        responsiveStyles: {
                          tablet: {
                            alignSelf: "center",
                            fontSize: "16px",
                            padding: "10px 27px",
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                          mobile: {
                            fontSize: "14px",
                            padding: "10px",
                          },
                        },
                      },
                      // Payment Methods
                      {
                        id: generateId(),
                        type: "image" as ElementType,
                        src: "/images/movyo/pay-option1.webp",
                        alt: "Payment Methods",
                        styles: {
                          maxWidth: "324px",
                          width: "100%",
                          objectFit: "contain",
                        },
                        responsiveStyles: {
                          tablet: { alignSelf: "center" },
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // Feature Box Text (Final CTA)
          {
            id: generateId(),
            type: "paragraph" as ElementType,
            content:
              "<a href='#'>No thanks, I understand this is my last chance and I'm okay with missing out.</a>",
            styles: {
              color: "link",
              textAlign: "center",
              fontFamily: "Inter",
              fontSize: "30px",
              fontStyle: "italic",
              fontWeight: "300",
              lineHeight: "100%",
              textDecoration: "underline",
              margin: "0",
            },
            responsiveStyles: {
              tablet: { fontSize: "20px" },
              mobile: { fontSize: "16px" },
            },
          },
        ],
      },
    ],
  };
};
export const template2Downsell = (globalConfig: GlobalConfig) => {
  return {
    banner: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        padding: "0",
        margin: "0",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
      children: [
        // Offer Header Section
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            backgroundImage: "url('/images/movyo/bannerBG.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            padding: "80px 0 55px",
            textAlign: "center",
            fontFamily: "Inter",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          responsiveStyles: {
            tablet: { padding: "60px 20px" },
            mobile: { padding: "40px 20px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "44px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { gap: "30px" },
                mobile: { gap: "20px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "logo" as ElementType,
                  styles: {
                    maxWidth: "385px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  responsiveStyles: {
                    tablet: { maxWidth: "200px" },
                    mobile: { maxWidth: "150px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/images/movyo/logo-white.svg",
                      alt: "Logo",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "WAIT",
                      styles: {
                        color: "background",
                        fontSize: "58.3px",
                        fontWeight: "700",
                        lineHeight: "58.3px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "30px", lineHeight: "30px" },
                        mobile: { fontSize: "20px", lineHeight: "20px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "THIS IS YOUR FINAL CHANCE",
                      styles: {
                        color: "background",
                        fontSize: "58.3px",
                        fontWeight: "700",
                        lineHeight: "58.3px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "30px", lineHeight: "30px" },
                        mobile: { fontSize: "20px", lineHeight: "20px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "DO NOT CLOSE THIS PAGE! IT MAY CAUSE AN ERROR IN YOUR CONFIRMATION!",
                  styles: {
                    color: "background",
                    fontSize: "27.3px",
                    fontWeight: "400",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "20px" },
                    mobile: { fontSize: "14px" },
                  },
                },
              ],
            },
          ],
        },
        // Offer Sub Header Section
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            backgroundColor: "#7B3DF5", // $gray-purple
            padding: "28.5px 0",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          responsiveStyles: {
            tablet: { padding: "20px 16px" },
            mobile: { padding: "20px 16px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "50px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { gap: "30px" },
                mobile: { gap: "20px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "3 Bottles Were Too Much? No Problem!",
                  styles: {
                    color: "background",
                    fontSize: "48px",
                    fontWeight: "700",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "38px" },
                    mobile: { fontSize: "20px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    offer: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        padding: "81px 0 66.63px",
        fontFamily: "SF Pro Display",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { padding: "35px 16px 50px" },
        mobile: { padding: "25px 16px 50px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            maxWidth: "1296px",
            margin: "0 auto",
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                flexDirection: "column",
                gap: "55px",
              },
              responsiveStyles: {
                tablet: { gap: "35px" },
                mobile: { gap: "25px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "It's Okay to Start Small...", // Replace with dynamic if needed
                  styles: {
                    color: "forground",
                    fontSize: "58.3px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "100%",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "38px" },
                    mobile: { fontSize: "30px" },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "55px",
                  },
                  responsiveStyles: {
                    tablet: { gap: "10px" },
                    mobile: { gap: "10px" },
                  },
                  children: [
                    // Example notes, replace with dynamic if needed
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "We get it - 3 extra bottles might've felt like too much right now. But before we wrap up your order, here's a lighter option.",
                      styles: {
                        color: "link",
                        fontSize: "30px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "36px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Why not grab just 2 more bottles at the same VIP rate - no pressure, just one last little boost to keep your results going strong.",
                      styles: {
                        color: "link",
                        fontSize: "30px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "36px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    features: template2DownsellFeature(globalConfig),
    features2: template2DownsellFeature(globalConfig),
    bonuses: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundColor: "background",
        width: "100%",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            maxWidth: "1450px",
            margin: "0 auto",
            padding: "0 50px",
          },
          responsiveStyles: {
            tablet: { maxWidth: "100%", padding: "0 20px" },
            mobile: { maxWidth: "100%", padding: "0 16px" },
          },
          children: [
            // Title and subtitle
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                maxWidth: "1200px",
                margin: "0 auto",
                textAlign: "center",
                padding: "72px 0 0 0",
              },
              responsiveStyles: {
                tablet: { padding: "50px 0 0 0" },
                mobile: { padding: "40px 0 0 0" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content:
                    "Unlock Exclusive Bonuses – Yours FREE With Your Order!",
                  styles: {
                    fontSize: "33px",
                    fontWeight: "700",
                    lineHeight: "49.5px",
                    color: "#forground",
                    paddingBottom: "24px",
                    textShadow: "0px 2px 0px rgba(0, 0, 0, 0.04)",
                    margin: "0",
                  },
                  typography: "Heading-4",
                  responsiveStyles: {
                    tablet: {
                      fontSize: "28px",
                      lineHeight: "36px",
                      paddingBottom: "20px",
                    },
                    mobile: {
                      fontSize: "24px",
                      lineHeight: "32px",
                      paddingBottom: "16px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "When you order MOVYO today, you not only get the secret to natural weight loss, you also receive exclusive bonuses designed to supercharge your transformation!",
                  styles: {
                    fontSize: "20px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    color: "forground",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px", lineHeight: "22px" },
                    mobile: { fontSize: "16px", lineHeight: "20px" },
                  },
                },
              ],
            },
            // Bonuses content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "81px",
                marginTop: "24px",
              },
              responsiveStyles: {
                tablet: { gap: "20px", marginTop: "20px" },
                mobile: {
                  flexDirection: "column",
                  gap: "30px",
                  marginTop: "16px",
                  alignItems: "center",
                },
              },
              children: [
                // Bonus 1
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "24px",
                    width: "294px",
                  },
                  responsiveStyles: {
                    tablet: { width: "100%", maxWidth: "400px", gap: "20px" },
                    mobile: { width: "100%", gap: "16px" },
                  },
                  children: [
                    // Image + Badge
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        width: "294px",
                        height: "294px",
                        borderRadius: "20px",
                        overflow: "hidden",
                        position: "relative",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "100%",
                          maxWidth: "300px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                        mobile: {
                          width: "100%",
                          maxWidth: "250px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/images/movyo/bonuse1.webp",
                          alt: "Bonus 1",
                          styles: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          },
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            bottom: "8px",
                            right: "8.5px",
                            width: "52px",
                            height: "52px",
                          },
                          responsiveStyles: {
                            tablet: { width: "40px", height: "40px" },
                            mobile: { width: "35px", height: "35px" },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/digitalBadge.webp",
                              alt: "Bonus 1 Badge",
                              styles: {
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Title and description
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content:
                            "Love Your Body Again <br>30-Day Reset Guide",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          typography: "Button",
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "This guide provides a simple, step-by-step plan to jumpstart your weight loss journey. Enjoy practical tips and easy routines to transform your body from day one.",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                      ],
                    },
                    // Amount
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {},
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            '<span style="text-decoration:line-through;">$47</span> Today: <span style="color:#D4339C;">FREE</span>',
                          styles: {
                            color: "tertiary",
                            fontSize: "28px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            textAlign: "center",
                            margin: "0",
                          },
                          typography: "Heading-5",
                          responsiveStyles: {
                            tablet: { fontSize: "24px", lineHeight: "28px" },
                            mobile: { fontSize: "22px", lineHeight: "26px" },
                          },
                        },
                      ],
                    },
                  ],
                },
                // Bonus 2
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "24px",
                    width: "294px",
                  },
                  responsiveStyles: {
                    tablet: { width: "100%", maxWidth: "400px", gap: "20px" },
                    mobile: { width: "100%", gap: "16px" },
                  },
                  children: [
                    // Image + Badge
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        width: "294px",
                        height: "294px",
                        borderRadius: "20px",
                        overflow: "hidden",
                        position: "relative",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "100%",
                          maxWidth: "300px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                        mobile: {
                          width: "100%",
                          maxWidth: "250px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/images/movyo/bonuse2.webp",
                          alt: "Bonus 2",
                          styles: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          },
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            bottom: "8px",
                            right: "5px",
                            width: "52px",
                            height: "52px",
                          },
                          responsiveStyles: {
                            tablet: { width: "40px", height: "40px" },
                            mobile: { width: "35px", height: "35px" },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/digitalBadge2.webp",
                              alt: "Bonus 2 Badge",
                              styles: {
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Title and description
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "Forever Fit <br>The Alpine Secret Guide",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          typography: "Button",
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "Discover key strategies to maintain energy, vitality, and a slim figure. Learn timeless French Alpine wisdom for sustainable, lasting results.",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                      ],
                    },
                    // Amount
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {},
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            '<span style="text-decoration:line-through;">$100</span> Today: <span style="color:#D4339C;">FREE</span>',
                          styles: {
                            color: "tertiary",
                            fontSize: "28px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            textAlign: "center",
                            margin: "0",
                          },
                          typography: "Heading-5",
                          responsiveStyles: {
                            tablet: { fontSize: "24px", lineHeight: "28px" },
                            mobile: { fontSize: "22px", lineHeight: "26px" },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    finalThought: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        padding: "107px 0",
        backgroundColor: "background",
        width: "100%",
        maxWidth: "1450px",
        margin: "0 auto",
      },
      responsiveStyles: {
        mobile: { padding: "50px 16px" },
        tablet: { padding: "80px 20px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "stretch",
            justifyContent: "space-between",
            width: "100%",
          },
          responsiveStyles: {
            mobile: {
              flexDirection: "column-reverse",
              gap: "30px",
              alignItems: "center",
            },
            tablet: { gap: "25px", alignItems: "center" },
          },
          children: [
            // Left (Image)
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "45%",
              },
              responsiveStyles: {
                mobile: { width: "100%", maxWidth: "300px" },
                tablet: { width: "50%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/images/movyo/upsell/final.svg",
                  alt: "Final Thought",
                  styles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                },
              ],
            },
            // Right (Content)
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "55%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "48px",
              },
              responsiveStyles: {
                mobile: { width: "100%", gap: "20px" },
                tablet: { width: "50%", gap: "25px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "This is your final opportunity!",
                  styles: {
                    color: "forground",
                    fontSize: "58.3px",
                    fontWeight: "700",
                    lineHeight: "70.033px",
                  },
                  responsiveStyles: {
                    mobile: {
                      fontSize: "24px",
                      lineHeight: "32px",
                      textAlign: "center",
                    },
                    tablet: { fontSize: "32px", lineHeight: "100%" },
                  },
                },
                // Note (list of paragraphs)
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "48px",
                  },
                  responsiveStyles: {
                    mobile: { gap: "16px" },
                    tablet: { gap: "20px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "I understand this might be your last chance to get MOVYO at this special pricing. Don't let this opportunity pass you by.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "16px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "28px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Your health and transformation are worth investing in. This is the moment to take action and commit to your goals.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "16px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "28px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Remember: The best time to start was yesterday. The second best time is now.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "16px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "28px" },
                      },
                    },
                  ],
                },
                // Signature
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "0",
                  },
                  responsiveStyles: {
                    mobile: { alignItems: "center" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "text" as ElementType,
                      content: "Dr. Jane Doe",
                      styles: {
                        color: "link",
                        fontFamily: "Allura",
                        fontSize: "42px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "100%",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "36px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Formulator of Movyo",
                      styles: {
                        color: "link",
                        fontFamily: "Inter",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "20px" },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    // Added sections from template2.ts
    mentioned: createMentionedInSection(globalConfig),
    partnerMenu: createPartnerMenuSection(globalConfig),
    listing: createListingSection(globalConfig),
    footerMenu: createFooterMenuSection(globalConfig),
    footerEnd: createFooterEndSection(globalConfig),
  };
};
